
import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { getDeviceInfo } from "@/utils/deviceInfo";
import { toast } from "sonner";

export function useSessionManager(userId: string | null, sessionId: string | null) {
  const [isSessionValid, setIsSessionValid] = useState(true);
  const [lastActivity, setLastActivity] = useState<Date>(new Date());

  // Record a new session in the database
  const recordSession = useCallback(async () => {
    if (!userId || !sessionId) return;

    try {
      const deviceInfo = getDeviceInfo();
      
      const { error } = await supabase
        .from('active_sessions')
        .upsert({
          user_id: userId,
          session_id: sessionId,
          device_info: deviceInfo,
          is_active: true,
        }, {
          onConflict: 'user_id,session_id'
        });

      if (error) {
        console.error("Error recording session:", error);
      } else {
        console.log("Session recorded successfully");
      }
    } catch (error) {
      console.error("Exception recording session:", error);
    }
  }, [userId, sessionId]);

  // Check if the current session is still valid
  const checkSessionValidity = useCallback(async () => {
    if (!userId || !sessionId) return true;
    
    try {
      const { data, error } = await supabase
        .from('active_sessions')
        .select('is_active, invalidated_by_new_session')
        .eq('user_id', userId)
        .eq('session_id', sessionId)
        .single();

      if (error) {
        console.error("Error checking session validity:", error);
        return true; // Default to valid on error to prevent unnecessary logouts
      }

      // If the session doesn't exist or is marked as inactive/invalidated
      if (!data || !data.is_active || data.invalidated_by_new_session) {
        console.log("Session is invalid:", data);
        setIsSessionValid(false);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Exception checking session validity:", error);
      return true; // Default to valid on error
    }
  }, [userId, sessionId]);

  // Update the last activity timestamp for this session
  const updateSessionActivity = useCallback(async () => {
    if (!userId || !sessionId) return;
    
    // Only update if it's been at least 1 minute since last update to reduce DB writes
    const now = new Date();
    if ((now.getTime() - lastActivity.getTime()) < 60000) return;

    try {
      // Check if the active_sessions table has a last_activity column
      const { error: columnCheckError } = await supabase
        .rpc('has_column', { 
          table_name: 'active_sessions', 
          column_name: 'last_activity' 
        });

      // If there was an error or the column doesn't exist, we won't update it
      if (!columnCheckError) {
        const { error } = await supabase
          .from('active_sessions')
          .update({ last_activity: now.toISOString() })
          .eq('user_id', userId)
          .eq('session_id', sessionId);

        if (error) {
          // Don't log this as error since it might just be missing column
          console.log("Note: Unable to update session activity - this is non-critical");
        } else {
          setLastActivity(now);
        }
      }
    } catch (error) {
      // This is non-critical functionality, so we'll just log it and continue
      console.log("Note: Session activity tracking not available");
    }
  }, [userId, sessionId, lastActivity]);

  // Handle an invalid session (logout and redirect)
  const handleInvalidSession = useCallback(() => {
    try {
      console.log("Handling invalid session");
      setIsSessionValid(false);
      
      // Clear Supabase session from local storage
      localStorage.removeItem('sb-fvoskhrltpygiduhzcgq-auth-token');
      
      // Show a toast notification
      toast.error("Your session has been invalidated. Please login again.");
      
      // Redirect to auth page after a brief delay
      setTimeout(() => {
        window.location.href = '/auth';
      }, 1500);
    } catch (error) {
      console.error("Error handling invalid session:", error);
      // Fallback redirect
      window.location.href = '/auth';
    }
  }, []);

  // Initial session validation check when hook mounts
  useEffect(() => {
    if (userId && sessionId) {
      checkSessionValidity().catch(error => {
        console.error("Initial session validation check failed:", error);
      });
    }
  }, [userId, sessionId, checkSessionValidity]);

  return {
    isSessionValid,
    recordSession,
    checkSessionValidity,
    updateSessionActivity,
    handleInvalidSession
  };
}
